/**
 * Copy for the comparison and use-case pages.
 *
 * Held as data rather than markup so each page type has one component and the
 * words live in one file — and, for the comparisons, so every claim about
 * another product sits in a single reviewable list rather than scattered
 * through JSX.
 *
 * Two rules for the comparison rows, both about not being wrong in public:
 *
 * A claim about a competitor states what their own published pricing page
 * says, carries the date it was read, and the page links that source so a
 * reader can check it. Pricing changes; an undated claim quietly becomes a
 * false one.
 *
 * A claim about FloorPoll states something the product already says on its own
 * landing page or does in this repository. In particular nothing here promises
 * an unlimited audience: no participant ceiling has been established either
 * way, and a comparison page is the worst place to invent one.
 */

/**
 * A cell that deliberately makes no claim.
 *
 * Used where the honest answer is that nothing was checked. Asserting that a
 * competitor lacks a feature is the easiest way to be wrong in public, and a
 * pricing page not mentioning something is not evidence it is absent.
 */
export const NO_CLAIM = '—';

export interface ComparisonRow {
  dimension: string;
  floorpoll: string;
  competitor: string;
}

export interface ComparisonPage {
  path: string;
  /** 50-60 characters, for the prerendered head and the SERP. */
  title: string;
  /** 150-160 characters, likewise. */
  description: string;
  competitor: string;
  heading: string;
  lede: string;
  rows: ComparisonRow[];
  /** When the competitor claims were last read from the sources below. */
  checked: string;
  /** Every page a competitor claim on this page was taken from. */
  sources: { url: string; label: string }[];
}

export const COMPARISONS: ComparisonPage[] = [
  {
    path: '/mentimeter-alternative',
    title: 'A simpler Mentimeter alternative for live polls',
    description:
      'Anonymous voting by default, your colours on the projector, and a six-character code your audience joins in one tap. See how FloorPoll compares.',
    competitor: 'Mentimeter',
    heading: 'A quieter alternative to Mentimeter',
    lede: 'Both put a question on a screen and let a room answer from their phones. The difference is what your audience has to do to take part, and what you have to pay before the poll looks like yours.',
    rows: [
      {
        dimension: 'What the audience does',
        floorpoll: 'Types six characters, or scans the code on the screen. No app, no account.',
        competitor: 'Joins with a code at menti.com. No app, no account.',
      },
      {
        dimension: 'Anonymity',
        floorpoll:
          'Anonymous by default. Answering asks for no name, email, phone number or location.',
        competitor: 'Also anonymous by default, with opt-in features for identifying participants.',
      },
      {
        dimension: 'Audience size on the free plan',
        floorpoll: '100 people per presentation, and the count starts again at the next one.',
        competitor:
          'The free plan covers 50 participants per month, with one unlimited session a month.',
      },
      {
        dimension: 'Lifting the audience cap',
        floorpoll: 'Any paid plan removes it. There is no larger tier to buy afterwards.',
        competitor:
          'The Basic plan removes the monthly participant limit, at US$14 per presenter per month billed yearly.',
      },
      {
        dimension: 'What arrives after a vote',
        floorpoll:
          'An option can carry a follow-up message or link, delivered only to the device that picked it.',
        // Deliberately no claim. Asserting that another product lacks a
        // feature is the easiest way to be wrong in public, and a pricing page
        // not mentioning something is not evidence it does not exist.
        competitor: NO_CLAIM,
      },
    ],
    checked: 'August 2026',
    sources: [
      { url: 'https://www.mentimeter.com/plans', label: 'mentimeter.com/plans' },
      { url: 'https://www.mentimeter.com/features/surveys', label: 'their surveys page' },
    ],
  },
  {
    path: '/slido-alternative',
    title: 'A simpler Slido alternative for audience polls',
    description:
      'Anonymous by default rather than an upgrade, and your own theme on every phone and the big screen. See how FloorPoll compares with Slido today.',
    competitor: 'Slido',
    heading: 'A simpler alternative to Slido',
    lede: 'Slido is built for the whole meeting — polls, Q&A, surveys, quizzes, integrations. FloorPoll is built for the room in front of you: a question on the screen, the answer as it lands, and the floor’s own questions moderated before they reach the projector. If polling is what you came for, that difference shows up in the price of the settings you actually want.',
    rows: [
      {
        dimension: 'What the audience does',
        floorpoll: 'Types six characters, or scans the code on the screen. No app, no account.',
        competitor: 'Joins with an event code at slido.com. No app, no account.',
      },
      {
        dimension: 'Controlling whether voters are named',
        floorpoll: 'Anonymous by default, on every poll.',
        competitor:
          'The participant-privacy setting, which controls whether people are named or anonymous by default, is on the Professional plan at US$75 a month billed annually.',
      },
      {
        dimension: 'Your colours on the screen',
        floorpoll: 'A theme that travels to the projector and to every phone.',
        competitor:
          'Custom themes for present and participant modes are on the Professional plan at US$75 a month billed annually.',
      },
      {
        dimension: 'Audience size',
        floorpoll:
          '100 people per presentation on the free plan. Paid plans are not capped at all.',
        competitor:
          'Capped per plan: 100 participants free, 200 on Engage at US$17.50 a month, 1,000 on Professional, 5,000 on Enterprise.',
      },
      {
        dimension: 'Scope',
        floorpoll:
          'Polls, screens, themes, follow-ups, moderated audience Q&A, and CSV exports of each run.',
        competitor: 'Polls plus Q&A, surveys, moderation and a wide integration list.',
      },
    ],
    checked: 'August 2026',
    sources: [{ url: 'https://www.slido.com/pricing', label: 'slido.com/pricing' }],
  },
  {
    path: '/poll-everywhere-alternative',
    title: 'A simpler Poll Everywhere alternative for live polls',
    description:
      'Poll Everywhere counts responses per poll and stops at 40 on the free plan. See how FloorPoll compares on audience size, branding and lifting the cap.',
    competitor: 'Poll Everywhere',
    heading: 'A simpler alternative to Poll Everywhere',
    lede: 'Poll Everywhere measures the room in responses per poll, so the same number that decides how many people can answer also decides whether anyone may answer twice. FloorPoll counts devices in one presentation, and only until you pay.',
    rows: [
      {
        dimension: 'What the audience does',
        floorpoll: 'Types six characters, or scans the code on the screen. No app, no account.',
        competitor:
          'Answers in the browser at the presenter’s pollev.com address, optionally setting a screen name shown beside their responses.',
      },
      {
        dimension: 'Audience size on the free plan',
        floorpoll: '100 people per presentation, and the count starts again at the next one.',
        competitor:
          'The free Intro plan gives “40 max audience size (700 for 30 days)”, counted as responses each single poll can receive.',
      },
      {
        dimension: 'Lifting the audience cap',
        floorpoll: 'Any paid plan removes it. There is no larger tier to buy afterwards.',
        competitor:
          'Present raises it to 700 max audience size, at $10 a month or $120 billed annually.',
      },
      {
        dimension: 'Taking the vendor’s name off the screen',
        floorpoll: 'A paid plan replaces the FloorPoll credit with your own colours and logo.',
        competitor:
          'Removing the Poll Everywhere logo from polls is part of custom branding on the Teams plan.',
      },
      {
        dimension: 'What arrives after a vote',
        floorpoll:
          'An option can carry a follow-up message or link, delivered only to the device that picked it.',
        competitor: NO_CLAIM,
      },
    ],
    checked: 'August 2026',
    sources: [{ url: 'https://www.polleverywhere.com/plans', label: 'polleverywhere.com/plans' }],
  },
  {
    path: '/kahoot-alternative',
    title: 'A calmer Kahoot alternative for live audience polls',
    description:
      'Kahoot! 360 scores the room and ranks it. FloorPoll asks a room what it thinks, anonymously, and stops counting the audience the moment you pay.',
    competitor: 'Kahoot!',
    heading: 'An alternative to Kahoot!, for asking rather than scoring',
    // Everything below was read from the Kahoot! 360 pricing page, which is
    // the business product. Claims are attributed to it by name rather than to
    // Kahoot! as a whole: the school and personal tiers are priced elsewhere
    // on their site and were not read, and a reader who knows those will spot
    // an unqualified claim about "Kahoot!" immediately.
    lede: 'Kahoot! 360 is built around the game: points for a correct answer, points lost for a wrong one, and someone wins. That is the right shape for training and the wrong shape for asking a room a question it might answer honestly. FloorPoll only does the second thing.',
    rows: [
      {
        dimension: 'What the room is doing',
        floorpoll:
          'Answering a question anonymously. No points, no leaderboard, and nobody has to invent a nickname.',
        competitor:
          'Playing a scored kahoot: participants get points for each correct answer, and lose all points for an incorrect one.',
      },
      {
        dimension: 'Audience size',
        floorpoll:
          '100 people per presentation on the free plan. Paid plans are not capped at all.',
        competitor:
          'Tiered the whole way up: 50 participants per session on Pro Start, 200 on Pro Standard, 1,000 on Pro Plus, 2,000 on Pro Max, and up to 5,000 on Pro Ultra with onboarding.',
      },
      {
        dimension: 'The free plan',
        floorpoll:
          'Runs real presentations for up to 100 people, carrying a “Powered by FloorPoll” credit on the join screen.',
        competitor:
          'The Kahoot! 360 pricing page prices the paid plans and links a free Kahoot! 360 Express account, stating no participant limit for it.',
      },
      {
        dimension: 'Your logo on the screen',
        floorpoll: 'Your colours and logo on the projector and every phone, on any paid plan.',
        competitor:
          'Adding your own company logo and branding is listed on Pro Max at $69 a month and Pro Ultra at $79 a month.',
      },
      {
        dimension: 'The cheapest way in',
        floorpoll:
          'Marquee is $144 a year or $15 a month. An Event Pass is $39 for 30 days with no subscription.',
        competitor: 'Pro Start is $19 a month, billed as $228 a year for one license.',
      },
    ],
    checked: 'August 2026',
    sources: [{ url: 'https://kahoot360.com/pricing/', label: 'kahoot360.com/pricing' }],
  },
  {
    path: '/ahaslides-alternative',
    title: 'An AhaSlides alternative with a bigger free plan',
    description:
      'AhaSlides holds 50 people free and 100 on its cheapest paid plan. FloorPoll holds 100 free, and the paid plan stops counting the room altogether.',
    competitor: 'AhaSlides',
    heading: 'An alternative to AhaSlides with more room to start',
    lede: 'AhaSlides prices the audience in steps: 50 free, 100 on the cheapest paid plan, 2,500 above that. FloorPoll gives you the whole first step for nothing and then stops taking the measurement.',
    rows: [
      {
        dimension: 'Audience size on the free plan',
        floorpoll: '100 people per presentation, and the count starts again at the next one.',
        competitor: 'Up to 50 participants.',
      },
      {
        dimension: 'What the first paid plan buys',
        floorpoll: 'Marquee, at $144 a year or $15 a month, removes the audience limit entirely.',
        competitor:
          'Essential, at $7.95 a month billed yearly, raises the audience to 100 participants and adds analytics and exports.',
      },
      {
        dimension: 'Audience size as you pay more',
        floorpoll: 'It stops being a number. There is no larger tier to buy afterwards.',
        competitor:
          'Pro at $15.95 a month billed yearly reaches 2,500 participants, and Enterprise is listed up to 100,000.',
      },
      {
        dimension: 'What you can ask on the free plan',
        floorpoll:
          'Questions grouped into screens — one to three per screen, up to ten options each.',
        competitor: 'All slide types, with 5 quiz and 3 unscored slides.',
      },
      {
        dimension: 'Your colours on the screen',
        floorpoll: 'Your colours and logo on the projector and every phone, on any paid plan.',
        competitor: 'Custom branding is on the Pro plan at $15.95 a month billed yearly.',
      },
    ],
    checked: 'August 2026',
    sources: [{ url: 'https://ahaslides.com/pricing/', label: 'ahaslides.com/pricing' }],
  },
  {
    path: '/vevox-alternative',
    title: 'A Vevox alternative that stops counting your audience',
    description:
      'Vevox and FloorPoll both hold 100 people on the free plan. The difference is what happens next — Vevox tiers up to 5,000, FloorPoll stops counting.',
    competitor: 'Vevox',
    heading: 'An alternative to Vevox that stops counting',
    lede: 'Vevox has one of the most generous free tiers in this category, and it holds exactly as many people as FloorPoll’s does. The comparison worth making is not the free plan — it is what the paid ones sell you, and whether the number ever ends.',
    rows: [
      {
        dimension: 'Audience size on the free plan',
        floorpoll: '100 people per presentation, and the count starts again at the next one.',
        competitor: 'Up to 100 participants in one session — the same number.',
      },
      {
        dimension: 'Audience size as you pay',
        floorpoll: 'Any paid plan removes the limit. There is no larger tier to buy afterwards.',
        competitor:
          'Up to 250 on Starter at €11.95 a month, 1,500 on Pro, and 5,000 on Enterprise.',
      },
      {
        dimension: 'Getting the results out',
        floorpoll: 'Every run is kept on its own and exports as CSV on a paid plan.',
        competitor:
          'Surveys and data exports start on Starter, at €11.95 a month or €143.40 billed annually.',
      },
      {
        dimension: 'Your colours on the screen',
        floorpoll: 'Your colours and logo on the projector and every phone, on any paid plan.',
        competitor: 'Branding and customization are on the Pro plan, listed at €24.95 a month.',
      },
      {
        dimension: 'Scope',
        floorpoll:
          'Polls, screens, themes, follow-ups, moderated audience Q&A, and CSV exports of each run.',
        competitor:
          'Polls plus Q&A, quizzes, surveys, word clouds, and Microsoft Teams and PowerPoint integrations.',
      },
    ],
    checked: 'August 2026',
    sources: [
      {
        url: 'https://www.vevox.com/pricing/business-pricing',
        label: 'vevox.com business pricing',
      },
    ],
  },
];

export interface UseCaseMoment {
  title: string;
  body: string;
}

export interface UseCasePage {
  path: string;
  /** 50-60 characters, for the prerendered head and the SERP. */
  title: string;
  /** 150-160 characters, likewise. */
  description: string;
  heading: string;
  lede: string;
  moments: UseCaseMoment[];
  /** A poll this room would actually run, shown as the worked example. */
  example: { question: string; options: string[] };
}

export const USE_CASES: UseCasePage[] = [
  {
    path: '/use-cases/town-halls',
    title: 'Live polls for all-hands and town hall meetings',
    description:
      'Ask the whole company something and get an honest answer. Anonymous voting by default, and every result on the big screen in under two seconds.',
    heading: 'Ask the whole company, and get an honest answer',
    lede: 'The hard part of an all-hands is not showing the slides. It is finding out what people actually think while they are all still in the room.',
    moments: [
      {
        title: 'Nobody has to put their hand up',
        body: 'Answering asks for no name, email address or location. People say what they think rather than what is safe to be seen thinking.',
      },
      {
        title: 'Remote and in the room, the same way',
        body: 'Everyone votes from the device already in their hand, so the people dialling in are not a second-class audience watching a show.',
      },
      {
        title: 'The answer is on the screen before the moment passes',
        body: 'Every vote reaches the big screen in under two seconds, so you can react to the result while the room is still thinking about the question.',
      },
      {
        title: 'The floor can ask, and you choose what goes up',
        body: 'Open Q&A and anyone in the room can send a question from their phone, without giving a name. A moderator sees each one first, and only what they approve reaches the big screen.',
      },
    ],
    example: {
      question: 'What should we spend the next quarter fixing?',
      options: ['Hiring speed', 'Meeting load', 'Tooling', 'On-call rota'],
    },
  },
  {
    path: '/use-cases/conferences',
    title: 'Audience polls for conferences and keynotes',
    description:
      'Put a question on the main stage and watch a room of strangers answer from their phones. No app to download, no account, and no queue to join in.',
    heading: 'Poll a room full of strangers, from the main stage',
    lede: 'A conference audience will not download anything for a single question. Six characters on the screen is the entire ask.',
    moments: [
      {
        title: 'No app, no account, no queue',
        body: 'Voting happens in whatever browser is already on the phone, so nobody spends your slot in an app store.',
      },
      {
        title: 'It looks like your event',
        body: 'Your colours and logo travel to the projector and to every phone, so the poll belongs to the conference rather than to a vendor.',
      },
      {
        title: 'Open a talk with the room, not a slide',
        body: 'Put the question up first and let the answer set the frame for the next thirty minutes.',
      },
    ],
    example: {
      question: 'How long have you been working with this stack?',
      options: ['First week', 'Under a year', 'One to three years', 'Longer than that'],
    },
  },
  {
    path: '/use-cases/classrooms',
    title: 'Live polls for classrooms and lectures',
    description:
      'Check whether the room followed you before you move on. Students join with six characters, answer anonymously, and never make an account at all.',
    heading: 'Find out whether the room followed you',
    lede: 'Asking "does that make sense?" gets nods. Asking the room to answer gets you the truth before you move on to the next idea.',
    moments: [
      {
        title: 'Quiet students answer too',
        body: 'Anonymous by default, so the answer comes from everyone rather than from the three people willing to speak up.',
      },
      {
        title: 'No accounts to administer',
        body: 'Students join with six characters and hand over no name, email address or location. There is no roster to maintain and nothing to reset in September.',
      },
      {
        title: 'Check the room, then keep the result',
        body: 'Each run is kept separately and exports as CSV, so a check at the start of term is still there to compare against at the end.',
      },
    ],
    example: {
      question: 'Which step of the proof lost you?',
      options: ['The substitution', 'The inequality', 'The limit', 'None, keep going'],
    },
  },
  {
    path: '/use-cases/team-offsites',
    title: 'Live polls for team offsites and workshops',
    description:
      'Decide the venue, the agenda or the retro theme in the room rather than in a thread afterwards. Everyone votes at once, anonymously, from their seat.',
    heading: 'Decide it in the room, not in the thread afterwards',
    lede: 'The point of getting everyone in one place is to settle things while they are all there. A poll turns twenty opinions into a decision in about a minute.',
    moments: [
      {
        title: 'Everyone answers at once',
        body: 'No going round the table, no loudest-voice-wins, and no one quietly disagreeing until the follow-up thread.',
      },
      {
        title: 'Several questions on one screen',
        body: 'One to three questions per screen and up to ten options each, so a whole agenda item can be settled in a single pass.',
      },
      {
        title: 'The result outlives the day',
        body: 'Votes accumulate across presentations and each run is kept on its own, so the decision is still there when someone asks in a month.',
      },
    ],
    example: {
      question: 'Where should we do the next offsite?',
      options: ['Lisbon', 'Reykjavík', 'Mexico City'],
    },
  },
];
